<template>
  <div class="container-fluid py-4">

    <!-- ========================= HEADER + FILTRES ========================= -->
    <div class="d-flex justify-content-between align-items-center mb-4 flex-wrap header-clinique">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-bar-chart-line fs-2 text-primary"></i>
        <h2 class="fw-bold mb-0">Tableau de bord</h2>
      </div>

      <!-- Filtres alignés à droite -->
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <div class="d-flex flex-wrap gap-2">
          <!-- Filtres principaux -->
          <button
            v-for="periode in periodesPrincipales"
            :key="periode.value"
            class="btn"
            :class="filtre === periode.value ? 'btn-clinique' : 'btn-outline-primary'"
            @click="changerFiltre(periode.value)"
          >
            {{ periode.label }}
          </button>

          <!-- Dropdown pour filtres secondaires -->
          <div class="dropdown">
            <button
              class="btn btn-outline-secondary dropdown-toggle"
              type="button"
              data-bs-toggle="dropdown"
            >
              Plus
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li v-for="periode in periodesSecondaires" :key="periode.value">
                <a
                  class="dropdown-item"
                  href="#"
                  @click.prevent="changerFiltre(periode.value)"
                >
                  {{ periode.label }}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- ========================= LOADER ========================= -->
    <div v-if="isLoading" class="text-center my-5">
      <div class="spinner-border text-primary" style="width: 3rem; height: 3rem;"></div>
      <p class="mt-2">Chargement des statistiques...</p>
    </div>

    <!-- ========================= CONTENU ========================= -->
    <div v-else>

      <!-- === SECTION PATIENTS === -->
      <div class="mb-5 section-separator">
        <h4 class="text-primary fw-bold mb-3">👤 Patients</h4>
        <div class="row g-3">
          <div
            class="col-12 col-sm-6 col-lg-3"
            v-for="box in patientsKpi"
            :key="box.title"
          >
            <div class="card shadow-sm card-kpi">
              <div class="card-body d-flex align-items-center gap-3">
                <div :class="['icon-circle', box.bg]"><i :class="box.icon"></i></div>
                <div>
                  <h6 class="text-muted small mb-1">{{ box.title }}</h6>
                  <h4 class="fw-bold mb-0">{{ box.value }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- === SECTION CONSULTATIONS === -->
      <div class="mb-5 section-separator">
        <h4 class="text-success fw-bold mb-3">🩺 Consultations</h4>
        <div class="row g-3">
          <div
            class="col-12 col-sm-6 col-lg-3"
            v-for="box in consultationsKpi"
            :key="box.title"
          >
            <div class="card shadow-sm card-kpi">
              <div class="card-body d-flex align-items-center gap-3">
                <div :class="['icon-circle', box.bg]"><i :class="box.icon"></i></div>
                <div>
                  <h6 class="text-muted small mb-1">{{ box.title }}</h6>
                  <h4 class="fw-bold mb-0">{{ box.value }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- === SECTION FINANCES === -->
      <div class="mb-5 section-separator">
        <h4 class="text-danger fw-bold mb-3">💰 Services</h4>
        <div class="row g-3">
          <div
            class="col-12 col-sm-6 col-lg-3"
            v-for="box in financesKpi"
            :key="box.title"
          >
            <div class="card shadow-sm card-kpi">
              <div class="card-body d-flex align-items-center gap-3">
                <div :class="['icon-circle', box.bg]"><i :class="box.icon"></i></div>
                <div>
                  <h6 class="text-muted small mb-1">{{ box.title }}</h6>
                  <h4 class="fw-bold mb-0">{{ box.value }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- === SECTION HOSPITALISATIONS === -->
      <div class="mb-5 section-separator">
        <h4 class="text-warning fw-bold mb-3">🏥 Hospitalisations</h4>
        <div class="row g-3">
          <div
            class="col-12 col-sm-6 col-lg-3"
            v-for="box in hospitalisationsKpi"
            :key="box.title"
          >
            <div
              class="card shadow-sm card-kpi"
              :class="box.alert ? 'border border-danger' : ''"
            >
              <div class="card-body d-flex align-items-center gap-3">
                <div :class="['icon-circle', box.bg]"><i :class="box.icon"></i></div>
                <div>
                  <h6 class="text-muted small mb-1">{{ box.title }}</h6>
                  <h4 class="fw-bold mb-0">{{ box.value }}</h4>
                  <span v-if="box.alert" class="badge bg-light text-danger mt-2">
                    ⚠️ {{ box.message }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- === SECTION PHARMACIE === -->
      <div class="mb-5 section-separator">
        <h4 class="text-info fw-bold mb-3">💊 Pharmacie</h4>
        <div class="row g-3">
          <div
            class="col-12 col-sm-6 col-lg-3"
            v-for="box in medicamentsKpi"
            :key="box.title"
          >
            <div class="card shadow-sm card-kpi">
              <div class="card-body d-flex align-items-center gap-3">
                <div :class="['icon-circle', box.bg]"><i :class="box.icon"></i></div>
                <div>
                  <h6 class="text-muted small mb-1">{{ box.title }}</h6>
                  <h4 class="fw-bold mb-0">{{ box.value }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      

    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  data() {
    return {
      filtre: 'jour',
      isLoading: false,
      totals: {},
      periodesPrincipales: [
        { value: 'jour', label: "Aujourd'hui" },
        { value: 'semaine', label: 'Cette semaine' },
        { value: 'mois', label: 'Ce mois-ci' },
        { value: 'annee', label: 'Cette année' }
      ],
      periodesSecondaires: [
        { value: 'hier', label: 'Hier' },
        { value: 'semaine_derniere', label: 'Semaine dernière' },
        { value: 'mois_dernier', label: 'Mois dernier' },
        { value: 'annee_derniere', label: 'Année dernière' }
      ]
    };
  },
  computed: {
    patientsKpi() {
      return [
        { title: 'Total Patients', value: this.totals.patients ?? 0, icon: 'bi bi-people', bg: 'bg-primary',route: { name: 'patients' } }
      ];
    },
    consultationsKpi() {
      return [
        { title: 'En cours', value: this.totals.consultation_en_cours ?? 0, icon: 'bi bi-hourglass-split', bg: 'bg-warning' },
        { title: 'En attente', value: this.totals.consultations_en_attente ?? 0, icon: 'bi bi-hourglass-split', bg: 'bg-warning' },
        { title: 'Terminées', value: this.totals.consultations_terminees ?? 0, icon: 'bi bi-check-circle', bg: 'bg-success' },
        { title: 'Total', value: this.totals.total_consultations ?? 0, icon: 'bi bi-clipboard-data', bg: 'bg-info' },
        { title: 'Montant payé patient', value: this.formatMontant(this.totals.montant_paye ?? 0), icon: 'bi bi-cash-stack', bg: 'bg-primary' },
        { title: 'Montant à payer Assurance', value: this.formatMontant(this.totals.montant_paye_assurance?? 0), icon: 'bi bi-cash-stack', bg: 'bg-secondary' },
        { title: 'Total', value: this.formatMontant((this.totals.montant_paye_assurance + this.totals.montant_paye) ?? 0), icon: 'bi bi-cash-stack', bg: 'bg-secondary' }
      ];
    },
     financesKpi() {
    return [
        {
      title: 'Nombre de factures',
      value: this.totals.nombre_factures ?? 0,
      icon: 'bi bi-receipt',
      bg: 'bg-info'
    },
       {
      title: 'Factures brouillons',
      value: this.totals.factures_brouillon ?? 0,
      icon: 'bi bi-file-earmark-text',
      bg: 'bg-secondary'
    },
    {
      title: 'Facture Partiellement payée',
      value: this.totals.factures_partiellement_payee ?? 0,
      icon: 'bi bi-hourglass-split',
      bg: 'bg-warning'
    },
     {
      title: 'Factures payées',
      value: this.totals.factures_payees ?? 0,
      icon: 'bi bi-check-circle',
      bg: 'bg-success'
    },
  {
      title: 'Nombre de services',
      value: this.totals.nombre_services ?? 0,
      icon: 'bi bi-list-check',
      bg: 'bg-primary'
    },
  
    {
      title: 'Services annulés',
      value: this.totals.services_annules ?? 0,
      icon: 'bi bi-x-circle',
      bg: 'bg-danger'
    },
     {
      title: 'Montant encaissé Assurance',
      value: this.formatMontant(this.totals.montant_encaisse_assurance ?? 0),
      icon: 'bi bi-building',
      bg: 'bg-primary'
    },
     {
      title: 'Montant encaissé Patient',
      value: this.formatMontant(this.totals.montant_encaisse_patient ?? 0),
      icon: 'bi bi-cash-coin',
      bg: 'bg-success'
    },
 
   
    {
      title: 'Montant encaissé',
      value: this.formatMontant(this.totals.montant_encaisse ?? 0),
      icon: 'bi bi-currency-exchange',
      bg: 'bg-dark'
    },
    {
      title: 'Montant à rembourser',
      value: this.formatMontant(this.totals.remboursement ?? 0),
      icon: 'bi bi-building',
      bg: 'bg-primary'
    },
    /*{
      title: 'Reste à encaisser a def',
      value: this.formatMontant(this.totals.reste_a_encaisser ?? 0),
      icon: 'bi bi-exclamation-circle',
      bg: (this.totals.reste_a_encaisser ?? 0) > 0 ? 'bg-warning' : 'bg-success'
    }*/
  ];
    },
    hospitalisationsKpi() {
      const sortieDepassee = this.totals.nbre_hospi_sortie_prevue ?? 0;

      return [
        { title: 'En cours', value: this.totals.hospitalisations_en_cours ?? 0, icon: 'bi bi-hospital', bg: 'bg-warning' },
        { title: 'Total Chambres', value: this.totals.total_chambres ?? 0, icon: 'bi bi-building', bg: 'bg-primary' },
        { title: 'Chambres occupées', value: this.totals.chambres_occupees ?? 0, icon: 'bi bi-door-closed', bg: 'bg-danger' },
        { title: 'Chambres libres', value: this.totals.chambres_libres ?? 0, icon: 'bi bi-door-open', bg: 'bg-success' },
        { title: 'Total lits', value: this.totals.total_lits ?? 0, icon: 'bi bi-bed', bg: 'bg-primary' },
        { title: 'Lits occupés', value: this.totals.lits_occupes ?? 0, icon: 'bi bi-person-fill', bg: 'bg-danger' },
        { title: 'Lits libres', value: this.totals.lits_libres ?? 0, icon: 'bi bi-bed', bg: 'bg-success' },
        {
          title: 'Sortie',
          value: sortieDepassee,
          icon: 'bi bi-exclamation-triangle-fill',
          bg: sortieDepassee > 0 ? 'bg-danger' : 'bg-success',
          alert: sortieDepassee > 0,
          message: sortieDepassee > 0 ? 'Attention : sorties prévues' : 'Aucune sortie en retard'
        },
      ];
    },
    medicamentsKpi() {
      return [
        { title: 'Stocks critiques', value: this.totals.stocks_critiques_count ?? 0, icon: 'bi bi-capsule', bg: 'bg-danger' }
      ];
    },
   
  },
  mounted() {
    this.charger();
  },
  methods: {
    changerFiltre(val) {
      this.filtre = val;
      this.charger();
    },
    async charger() {
      this.isLoading = true;
      try {
        const res = await api.getItems(`dashboard_data?periode=${this.filtre}`);
        this.totals = res?.data ?? {};
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoading = false;
      }
    },
    formatMontant(n) {
      return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`;
    }
  }
};
</script>

<style scoped>
/* SECTION HEADER ET FILTRES */
.header-clinique {
  padding: 15px 20px;
  background-color: #e9f5ff;
  border-radius: 12px;
  flex-wrap: wrap;
  gap: 10px;
}

/* BOUTONS */
.btn-clinique {
  background: linear-gradient(90deg, #0d6efd, #6610f2);
  border: none;
  color: #fff;
  border-radius: 10px;
}

/* CARTES KPI */
.card-kpi {
  border-radius: 12px;
  background: #fff;
  transition: transform 0.2s ease;
  border: none;
}
.card-kpi:hover {
  transform: translateY(-4px);
}

/* ICÔNES RONDES */
.icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.25rem;
}

/* SECTION VISUELLE */
.section-separator {
  border-top: 4px solid #ccc;
  padding-top: 20px;
  margin-top: 40px;
}

/* RESPONSIVITÉ */
@media (max-width: 768px) {
  .header-clinique {
    flex-direction: column;
    text-align: center;
  }
  .card-kpi {
    text-align: center;
  }
  .icon-circle {
    margin: 0 auto;
  }
}
</style>
